import { useState, useRef } from "react";
import { Autocomplete, Box, ClickAwayListener, TextField, styled } from "@mui/material";
import {
  ExpandMore as ChevronIcon,
  PeopleAlt as PeopleIcon,
} from "@mui/icons-material";
import { RTL } from "../../../cache/CacheRtl";
import { t } from "./coachTokens";

// Status options for the clients tab. Values match the existing
// clientsStateFilter state shape (so KPI drill-downs keep working).
// `null` is the "all statuses" choice. `color` drives a small dot next to
// each label so the dropdown stays scannable without emojis.
const STATUS_OPTS = [
  { value: null, label: "כל הסטטוסים", color: null },
  { value: "ACTIVE", label: "פעיל", color: t.green },
  { value: "IN_ACTIVE", label: "לא פעיל", color: t.t3 },
  { value: "CLOSE_TO_END", label: "עומד להסיים", color: t.t2 },
  { value: "DAYS_SINCE_30_PLUS", label: "נעדרים", color: t.red },
];

const StatusDot = ({ color }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: color,
      flexShrink: 0,
    }}
  />
);

// Pill-shaped button matching the mockup's `csfBtn`.
const PillButton = styled("button")(() => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "4px 11px",
  borderRadius: 20,
  border: `1px solid ${t.border}`,
  background: t.white,
  color: t.t2,
  fontSize: 11.5,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "inherit",
  transition: "all .13s",
  height: 28,
  whiteSpace: "nowrap",
  "&:hover": { borderColor: t.t3, color: t.text },
}));

// Dropdown option row matching `.csf-opt`.
const OptionRow = styled(Box, {
  shouldForwardProp: (p) => p !== "selected",
})(({ selected }) => ({
  padding: "8px 14px",
  fontSize: 12.5,
  cursor: "pointer",
  color: selected ? t.greenText : t.text,
  background: selected ? t.greenLight : "transparent",
  fontWeight: selected ? 600 : 400,
  transition: "background .1s",
  whiteSpace: "nowrap",
  display: "flex",
  alignItems: "center",
  gap: 6,
  "&:hover": { background: selected ? t.greenLight : t.bg },
}));

// Trainer Autocomplete styled to match the pill aesthetic
const TrainerSelect = styled(Autocomplete)({
  minWidth: 160,
  "& .MuiOutlinedInput-root": {
    color: t.t2,
    borderRadius: 20,
    backgroundColor: t.white,
    fontSize: "11.5px",
    fontWeight: 500,
    height: 28,
    paddingLeft: "11px !important",
    paddingRight: "11px !important",
    "& .MuiOutlinedInput-notchedOutline": { border: `1px solid ${t.border}` },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.t3 },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: t.green, borderWidth: "1px" },
  },
  "& .MuiInputLabel-root": {
    fontSize: "11.5px",
    color: t.t2,
    transform: "translate(14px, 6px) scale(1)",
    "&.MuiInputLabel-shrink": { transform: "translate(14px, -7px) scale(0.85)" },
    "&.Mui-focused": { color: t.green },
  },
});

const StatusPillDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const current = STATUS_OPTS.find((o) => o.value === value) || STATUS_OPTS[0];

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: "relative" }} ref={anchorRef}>
        <PillButton onClick={() => setOpen((o) => !o)} type="button">
          <PeopleIcon sx={{ fontSize: 12 }} />
          {current.color && <StatusDot color={current.color} />}
          <span>{current.label}</span>
          <ChevronIcon
            sx={{
              fontSize: 14,
              transition: "transform .15s",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </PillButton>
        {open && (
          <Box
            sx={{
              position: "absolute",
              top: "calc(100% + 4px)",
              insetInlineEnd: 0,
              background: t.white,
              border: `1px solid ${t.border}`,
              borderRadius: `${t.rs}`,
              boxShadow: t.sh,
              minWidth: 170,
              zIndex: 1200,
              overflow: "hidden",
            }}
          >
            {STATUS_OPTS.map((opt) => (
              <OptionRow
                key={opt.value ?? "all"}
                selected={opt.value === value}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.color && <StatusDot color={opt.color} />}
                <span>{opt.label}</span>
              </OptionRow>
            ))}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

const ClientsTabFilters = ({ state, onState, trainerId, onTrainer, trainers, showTrainerFilter }) => {
  return (
    <>
      <StatusPillDropdown value={state} onChange={onState} />
      {showTrainerFilter && trainers.length > 0 && (
        <RTL>
          <TrainerSelect
            size="small"
            options={[{ id: 0, name: "הכול" }, ...trainers.slice().sort((a, b) => a.name.localeCompare(b.name, "he"))]}
            getOptionLabel={(o) => o.name || ""}
            value={trainers.find((tr) => tr.id === trainerId) || { id: 0, name: "הכול" }}
            onChange={(_, v) => onTrainer(v?.id || 0)}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            disableClearable
            renderInput={(p) => <TextField {...p} label="מאמן" size="small" />}
            slotProps={{
              popper: { sx: { zIndex: 1300 } },
              paper: { sx: { borderRadius: t.rs, border: `1px solid ${t.border}`, boxShadow: t.sh } },
            }}
          />
        </RTL>
      )}
    </>
  );
};

export default ClientsTabFilters;
